import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TestimonialSection = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playingVideo, setPlayingVideo] = useState(null);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => 
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-8 lg:p-12 mb-12">
      <div className="text-center mb-8">
        <h2 className="font-headline font-bold text-3xl text-card-foreground mb-4">
          What Our Clients Say
        </h2>
        <p className="text-muted-foreground text-lg">
          Authentic testimonials from successful project transformations
        </p>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="relative">
          {/* Main Testimonial */}
          <div className="bg-card rounded-lg construction-shadow p-8 text-center">
            {/* Quote Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <Icon name="Quote" size={24} className="text-primary" />
            </div>

            {/* Testimonial Content */}
            <blockquote className="text-xl lg:text-2xl text-card-foreground font-medium leading-relaxed mb-8">
              "{currentTestimonial.content}"
            </blockquote>

            {/* Client Info */}
            <div className="flex items-center justify-center space-x-4 mb-6">
              <Image
                src={currentTestimonial.client.avatar}
                alt={currentTestimonial.client.name}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div className="text-left">
                <p className="font-bold text-lg text-card-foreground">
                  {currentTestimonial.client.name}
                </p>
                <p className="text-muted-foreground">
                  {currentTestimonial.client.title}
                </p>
                <p className="text-sm text-muted-foreground flex items-center space-x-1">
                  <Icon name="MapPin" size={14} />
                  <span>{currentTestimonial.client.location}</span>
                </p>
              </div>
            </div>

            {/* Rating */}
            <div className="flex items-center justify-center space-x-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Icon
                  key={i}
                  name="Star"
                  size={20}
                  className={`${
                    i < currentTestimonial.rating 
                      ? 'text-warning fill-current' :'text-muted-foreground/30'
                  }`}
                />
              ))}
            </div>

            {/* Video Testimonial */}
            {currentTestimonial.hasVideo && (
              <div className="mb-6">
                {playingVideo === currentIndex ? (
                  <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                    <iframe
                      src={currentTestimonial.videoUrl}
                      title={`${currentTestimonial.client.name} - Video Testimonial`}
                      className="w-full h-full"
                      allowFullScreen
                    />
                    <button
                      onClick={() => setPlayingVideo(null)}
                      className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-construction"
                    >
                      <Icon name="X" size={16} className="text-white" />
                    </button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    iconName="Play"
                    iconPosition="left"
                    onClick={() => setPlayingVideo(currentIndex)}
                  >
                    Watch Video Testimonial
                  </Button>
                )}
              </div>
            )}

            {/* Project Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-lg font-bold text-primary">{currentTestimonial.projectDetails.type}</p>
                <p className="text-xs text-muted-foreground">Project Type</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-lg font-bold text-success">{currentTestimonial.projectDetails.timeline}</p>
                <p className="text-xs text-muted-foreground">Completion</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-lg font-bold text-secondary">₹{currentTestimonial.projectDetails.budget}</p>
                <p className="text-xs text-muted-foreground">Budget</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-lg font-bold text-warning">₹{currentTestimonial.projectDetails.savings}</p>
                <p className="text-xs text-muted-foreground">Savings</p>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevTestimonial}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white construction-shadow hover:construction-shadow-lg rounded-full flex items-center justify-center transition-construction"
          >
            <Icon name="ChevronLeft" size={20} className="text-primary" />
          </button>
          <button
            onClick={nextTestimonial}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white construction-shadow hover:construction-shadow-lg rounded-full flex items-center justify-center transition-construction"
          >
            <Icon name="ChevronRight" size={20} className="text-primary" />
          </button>
        </div>

        {/* Testimonial Indicators */}
        <div className="flex items-center justify-center space-x-2 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-3 h-3 rounded-full transition-construction ${
                index === currentIndex ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
            />
          ))}
        </div>

        {/* All Testimonials Preview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          {testimonials.map((testimonial, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`p-4 rounded-lg text-left transition-construction ${
                index === currentIndex 
                  ? 'bg-primary/10 border-2 border-primary' :'bg-card hover:bg-muted border-2 border-transparent'
              }`}
            >
              <div className="flex items-center space-x-3 mb-2">
                <Image
                  src={testimonial.client.avatar}
                  alt={testimonial.client.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium text-sm">{testimonial.client.name}</p>
                  <p className="text-xs text-muted-foreground">{testimonial.client.location}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2">
                "{testimonial.content.substring(0, 100)}..."
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TestimonialSection;